/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import {
    ServiceCashboxesListItem,
    DeviceServiceListItem,
    RoughCashboxesListData,
    RoughCashboxesListItemDto,
    CashBoxTaskType,
    DevicePlatform,
    CashBoxTaskStatus,
    CashBoxTaskItemDto,
} from '../api/service/serviceClient.typings';
import { ServiceActions, ServiceActionType } from '../actions/serviceActions';
import { CashBoxesStatus, CashBoxTypes } from '../api/cashBoxes/cashBoxesClient.typings';
import { getPageLimitByScreenSize, sortVersionsStringArray } from '../../utils/utils';
import { RoughCashboxesListTableData } from '../../components/Service/Common/AddCashboxesModal/AddCashboxesModalConfig';

export const defaultSearchType: keyof RoughCashboxesListTableData = 'taxNumber';
export const defaultCashBoxesStatusesFilter: CashBoxesStatus[] = [
    'REGISTRATION',
    'ACTIVE',
    'BLOCKED',
    'RESET',
    'BROKEN',
    'SERVICE',
];

export interface ServiceState {
    serviceCashboxesList: Map<string, ServiceCashboxesListItem>; //taxNumber is a key
    roughCashboxesListData: RoughCashboxesListData;
    isLoadingCreateTasksResponse: boolean;
    isLoadingCashboxTasksStatusesUpdate: boolean;
    isLoadingServiceCashboxesListUpdate: boolean;
    isLoadingDevicesList: boolean;
    lpcFwTargetVersions: string[];
    stmFwTargetVersions: string[];
    unionFwTargetVersions: string[];
    intersectionFwTargetVersions: string[];
    isFileLoading: boolean;
    devicesList: DeviceServiceListItem[];
    selectedDevicesFromTable: string[];
    isLoadingFactoryNumbers: boolean;
    factoryNumbersList: string[];
    selectedFactoryNumbersList: string[];
    currentTab: number;
}

export const initialState: ServiceState = {
    serviceCashboxesList: new Map<string, ServiceCashboxesListItem>(),
    roughCashboxesListData: {
        isLoading: false,
        cashboxes: [],
        filterParams: {
            search_type: defaultSearchType,
            search_text: '',
            online: true,
            cashBoxTypes: [CashBoxTypes.PHYSICAL],
            cashBoxStatuses: [...defaultCashBoxesStatusesFilter],
        },
        changedFilterParams: null,
        limit: getPageLimitByScreenSize() * 2,
        afterKeyTaxNumber: null,
        lastRequestItemsCount: null,
    },
    isLoadingCreateTasksResponse: false,
    isLoadingServiceCashboxesListUpdate: false,
    isLoadingCashboxTasksStatusesUpdate: false,
    lpcFwTargetVersions: [],
    stmFwTargetVersions: [],
    unionFwTargetVersions: [],
    intersectionFwTargetVersions: [],
    isFileLoading: false,
    isLoadingDevicesList: false,
    devicesList: [],
    selectedDevicesFromTable: [],
    isLoadingFactoryNumbers: false,
    factoryNumbersList: [],
    selectedFactoryNumbersList: [],
    currentTab: 0,
};

function serviceReducer(state: ServiceState = initialState, action: ServiceActions): ServiceState {
    switch (action.type) {
        case ServiceActionType.OPEN_SERVICE_PAGE:
            return state; //TODO if needed

        case ServiceActionType.CHANGE_TAB:
            return {
                ...state,
                currentTab: action.payload,
            };

        case ServiceActionType.OPEN_ADD_CASHBOXES_DIALOG:
            return {
                ...state,
                roughCashboxesListData: {
                    ...state.roughCashboxesListData,
                    cashboxes: [],
                    afterKeyTaxNumber: null,
                    lastRequestItemsCount: null,
                },
            };

        case ServiceActionType.CLOSE_ADD_CASHBOXES_DIALOG:
            return {
                ...state,
                roughCashboxesListData: {
                    ...state.roughCashboxesListData,
                    isLoading: false,
                    changedFilterParams: null,
                },
            };

        case ServiceActionType.FETCH_ROUGH_CASHBOXES_LIST_NEXT_PAGE:
            return {
                ...state,
                roughCashboxesListData: {
                    ...state.roughCashboxesListData,
                    isLoading: true,
                    changedFilterParams: null,
                },
            };

        case ServiceActionType.APPLY_ROUGH_CASHBOXES_LIST_FILTERS:
            return {
                ...state,
                roughCashboxesListData: {
                    ...state.roughCashboxesListData,
                    isLoading: true,
                    cashboxes: [],
                    afterKeyTaxNumber: null,
                    lastRequestItemsCount: null,
                    filterParams: state.roughCashboxesListData.changedFilterParams
                        ? { ...state.roughCashboxesListData.changedFilterParams }
                        : { ...state.roughCashboxesListData.filterParams },
                    changedFilterParams: null,
                },
            };

        case ServiceActionType.CHANGE_ROUGH_CASHBOXES_LIST_FILTERS:
            return {
                ...state,
                roughCashboxesListData: {
                    ...state.roughCashboxesListData,
                    changedFilterParams: action.payload,
                },
            };

        case ServiceActionType.FETCH_ROUGH_CASHBOXES_LIST_PAGE_SUCCESS:
            const combinedList: RoughCashboxesListItemDto[] = [...state.roughCashboxesListData.cashboxes];
            if (action.payload.list.cashBoxes && action.payload.list.cashBoxes.length > 0) {
                combinedList.push(...action.payload.list.cashBoxes);
            }
            return {
                ...state,
                roughCashboxesListData: {
                    ...state.roughCashboxesListData,
                    cashboxes: combinedList,
                    afterKeyTaxNumber: action.payload.list.afterKeyTaxNumber,
                    lastRequestItemsCount: action.payload.list.cashBoxes.length,
                    isLoading: false,
                },
            };

        case ServiceActionType.FETCH_ROUGH_CASHBOXES_LIST_PAGE_ERROR:
            return {
                ...state,
                roughCashboxesListData: {
                    ...state.roughCashboxesListData,
                    isLoading: false,
                },
            };

        case ServiceActionType.ADD_CASHBOXES_FOR_SERVICE: {
            const combinedList: Map<string, ServiceCashboxesListItem> = new Map(state.serviceCashboxesList);

            if (action.payload && action.payload.length > 0) {
                action.payload.forEach((item) => {
                    if (!combinedList.has(item.taxNumber))
                        combinedList.set(item.taxNumber, {
                            ...item,
                            selected: false,
                            certificate: null,
                            firmware: null,
                            logs: null,
                            envInfo: null,
                            dump: null,
                            repair: null,
                        });
                });
            }
            return {
                ...state,
                serviceCashboxesList: combinedList,
            };
        }

        case ServiceActionType.DELETE_ALL_CASHBOXES_FOR_SERVICE: {
            return {
                ...state,
                serviceCashboxesList: new Map<string, ServiceCashboxesListItem>(),
            };
        }

        case ServiceActionType.DELETE_CASHBOX_FOR_SERVICE: {
            const newList: Map<string, ServiceCashboxesListItem> = new Map(state.serviceCashboxesList);
            if (action.payload) newList.delete(action.payload);
            return {
                ...state,
                serviceCashboxesList: newList,
            };
        }

        case ServiceActionType.SELECT_ALL_CASHBOXES_FOR_SERVICE: {
            const newList: Map<string, ServiceCashboxesListItem> = new Map(state.serviceCashboxesList);
            newList.forEach((item) => (item.selected = action.payload));
            return {
                ...state,
                serviceCashboxesList: newList,
            };
        }

        case ServiceActionType.SELECT_CASHBOX_FOR_SERVICE: {
            const newList: Map<string, ServiceCashboxesListItem> = new Map(state.serviceCashboxesList);
            if (action.payload) {
                const selectedCashbox = newList.get(action.payload.taxNumber);
                selectedCashbox.selected = action.payload.isSelected;
            }
            return {
                ...state,
                serviceCashboxesList: newList,
            };
        }

        case ServiceActionType.CREATE_CASHBOXES_TASKS:
            return {
                ...state,
                isLoadingCreateTasksResponse: true,
            };

        case ServiceActionType.CREATE_CASHBOXES_TASKS_SUCCESS: {
            const updatedList: Map<string, ServiceCashboxesListItem> = new Map(state.serviceCashboxesList);

            if (action.payload.response && action.payload.response.length > 0) {
                action.payload.response.forEach((item) => {
                    const cashbox = updatedList.get(item.cashBoxTaxNumber);
                    if (cashbox) {
                        let taskData: Partial<CashBoxTaskItemDto> = { id: item.id };
                        if (item.id === null)
                            taskData = {
                                id: null,
                                status: CashBoxTaskStatus.REJECTED,
                                statusDetails: item.errorMessage ? item.errorMessage : null,
                            };
                        switch (item.taskName) {
                            case CashBoxTaskType.CERTUPDATE:
                                cashbox.certificate = taskData;
                                break;
                            case CashBoxTaskType.FWUPDATE:
                                cashbox.firmware = taskData;
                                break;
                            case CashBoxTaskType.LOGS:
                                cashbox.logs = taskData;
                                break;
                            case CashBoxTaskType.ENVINFO:
                                cashbox.envInfo = taskData;
                                break;
                            case CashBoxTaskType.DUMP:
                                cashbox.dump = taskData;
                                break;
                            case CashBoxTaskType.REPAIR:
                                cashbox.repair = taskData;
                                break;
                        }
                    }
                });
            }
            return {
                ...state,
                isLoadingCreateTasksResponse: false,
                serviceCashboxesList: updatedList,
                isLoadingServiceCashboxesListUpdate: true,
            };
        }

        case ServiceActionType.CREATE_CASHBOXES_TASKS_ERROR:
            return {
                ...state,
                isLoadingCreateTasksResponse: false,
            };

        case ServiceActionType.GET_CASHBOXES_TASKS_STATUSES:
            return {
                ...state,
                isLoadingCashboxTasksStatusesUpdate: true,
            };

        case ServiceActionType.GET_CASHBOXES_TASKS_STATUSES_SUCCESS: {
            const updatedList: Map<string, ServiceCashboxesListItem> = new Map(state.serviceCashboxesList);
            if (action.payload?.items?.length > 0) {
                action.payload.items.forEach((task) => {
                    const cashbox = updatedList.get(task.cashBoxTaxNumber);
                    if (cashbox) {
                        switch (task.name) {
                            case CashBoxTaskType.CERTUPDATE:
                                cashbox.certificate = { ...task };
                                break;
                            case CashBoxTaskType.FWUPDATE:
                                cashbox.firmware = { ...task };
                                break;
                            case CashBoxTaskType.LOGS:
                                cashbox.logs = { ...task };
                                break;
                            case CashBoxTaskType.ENVINFO:
                                cashbox.envInfo = { ...task };
                                break;
                            case CashBoxTaskType.DUMP:
                                cashbox.dump = { ...task };
                                break;
                            case CashBoxTaskType.REPAIR:
                                cashbox.repair = { ...task };
                                break;
                        }
                    }
                });
            }
            return {
                ...state,
                isLoadingCashboxTasksStatusesUpdate: false,
                serviceCashboxesList: updatedList,
            };
        }

        case ServiceActionType.GET_CASHBOXES_TASKS_STATUSES_ERROR:
            return {
                ...state,
                isLoadingCashboxTasksStatusesUpdate: false,
            };

        case ServiceActionType.UPDATE_SERVICE_CASHBOXES_DATA:
            return {
                ...state,
                isLoadingServiceCashboxesListUpdate: true,
            };

        case ServiceActionType.UPDATE_SERVICE_CASHBOXES_DATA_SUCCESS:
            const updatedList: Map<string, ServiceCashboxesListItem> = new Map(state.serviceCashboxesList);

            if (action.payload.cashBoxes && action.payload.cashBoxes.length > 0) {
                action.payload.cashBoxes.forEach((item) => {
                    const cashbox = updatedList.get(item.taxNumber);
                    if (cashbox) {
                        cashbox.firmwareVersion = item.firmwareVersion;
                        cashbox.fiscalDriverVersion = item.fiscalDriverVersion;
                        cashbox.status = item.status;
                        cashbox.online = item.online;
                        cashbox.errorCategory = item.errorCategory;
                        cashbox.errorType = item.errorType;
                        cashbox.operatorName = item.operatorName;
                        cashbox.operatorTaxNumber = item.operatorTaxNumber;
                    }
                });
            }
            return {
                ...state,
                isLoadingServiceCashboxesListUpdate: false,
            };

        case ServiceActionType.UPDATE_SERVICE_CASHBOXES_DATA_ERROR:
            return {
                ...state,
                isLoadingServiceCashboxesListUpdate: false,
            };

        case ServiceActionType.FETCH_SUITABLE_FW_TARGET_VERSIONS_SUCCESS: {
            if (action.payload.devicePlatform === DevicePlatform.LPC)
                return {
                    ...state,
                    lpcFwTargetVersions: sortVersionsStringArray([...action.payload.versions]),
                    unionFwTargetVersions: sortVersionsStringArray([
                        ...new Set([...state.stmFwTargetVersions, ...action.payload.versions]),
                    ]),
                    intersectionFwTargetVersions: sortVersionsStringArray(
                        action.payload.versions.filter((item) => state.stmFwTargetVersions.includes(item)),
                    ),
                };
            else
                return {
                    ...state,
                    stmFwTargetVersions: sortVersionsStringArray([...action.payload.versions]),
                    unionFwTargetVersions: sortVersionsStringArray([
                        ...new Set([...state.lpcFwTargetVersions, ...action.payload.versions]),
                    ]),
                    intersectionFwTargetVersions: sortVersionsStringArray(
                        action.payload.versions.filter((item) => state.lpcFwTargetVersions.includes(item)),
                    ),
                };
        }

        case ServiceActionType.EXIT_SERVICE_PAGE:
            return {
                ...state,
                //currentTab: 0, //leave open tab
                isLoadingCreateTasksResponse: false,
                isLoadingServiceCashboxesListUpdate: false,
                isLoadingCashboxTasksStatusesUpdate: false,
            };

        case ServiceActionType.UPLOAD_FILE_START:
            return {
                ...state,
                isFileLoading: true,
            };

        case ServiceActionType.UPLOAD_FILE_SUCCESS:
            return {
                ...state,
                isFileLoading: false,
            };

        case ServiceActionType.UPLOAD_FILE_ERROR:
            return {
                ...state,
                isFileLoading: false,
            };

        case ServiceActionType.FETCH_FACTORY_NUMBER_LIST:
            return {
                ...state,
                isLoadingFactoryNumbers: true,
            };

        case ServiceActionType.FETCH_FACTORY_NUMBER_LIST_SUCCESS:
            return {
                ...state,
                isLoadingFactoryNumbers: false,
                factoryNumbersList: action.payload,
            };

        case ServiceActionType.FETCH_FACTORY_NUMBER_LIST_ERROR:
            return {
                ...state,
                isLoadingFactoryNumbers: false,
            };

        case ServiceActionType.CLOSE_ADD_DEVICE_MODAL_LIST:
            return {
                ...state,
                factoryNumbersList: [],
                isLoadingFactoryNumbers: false,
            };

        case ServiceActionType.FETCH_DEVICES_LIST:
            return {
                ...state,
                factoryNumbersList: [],
                isLoadingDevicesList: true,
            };

        case ServiceActionType.FETCH_DEVICES_LIST_SUCCESS:
            return {
                ...state,
                devicesList: action.payload,
                isLoadingDevicesList: false,
            };

        case ServiceActionType.FETCH_DEVICES_LIST_ERROR:
            return {
                ...state,
                isLoadingDevicesList: false,
            };

        case ServiceActionType.ADD_DEVICE_TO_LIST:
            return {
                ...state,
                selectedFactoryNumbersList: [...state.selectedFactoryNumbersList, action.payload],
            };

        case ServiceActionType.DELETE_DEVICES_FROM_LIST:
            return {
                ...state,
                selectedFactoryNumbersList: action.payload,
                selectedDevicesFromTable: [],
                devicesList: action.payload.length === 0 ? [] : [...state.devicesList],
            };

        case ServiceActionType.SELECT_DEVICE_FROM_TABLE:
            return {
                ...state,
                selectedDevicesFromTable: action.payload,
            };

        case ServiceActionType.PERFORM_DEVICES_OPERATION:
            return {
                ...state,
                isLoadingDevicesList: true,
            };

        case ServiceActionType.PERFORM_DEVICES_OPERATION_SUCCESS:
            return {
                ...state,
                isLoadingDevicesList: false,
            };

        case ServiceActionType.PERFORM_DEVICES_OPERATION_ERROR:
            return {
                ...state,
                isLoadingDevicesList: false,
            };

        default:
            return state;
    }
}

export default serviceReducer;
